import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { 
  Sparkles, Send, AlertTriangle, Bot, User, Clock,
  TrendingUp, TrendingDown, Users, Wallet, ShieldCheck,
  Bell, BellRing, CheckCircle2, XCircle, AlertCircle,
  RefreshCcw, Settings, PlayCircle, Eye, ChevronRight,
  Activity, UserX, Crown, DollarSign, Target, Loader2,
  Filter, Search, Download, BarChart3, PieChartIcon,
  ArrowUpRight, ArrowDownRight, Zap, Save, Info
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '../utils/apiUrl';

// Color palette
const SEVERITY_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e'
};

const STATUS_COLORS = {
  new: '#3b82f6',
  acknowledged: '#f59e0b',
  resolved: '#22c55e',
  escalated: '#ef4444'
};

const INSIGHT_ICONS = {
  churn_risk: UserX,
  dormant_client: Clock,
  high_value_client: Crown,
  revenue_leakage: DollarSign,
  dealer_productivity: Target
};

// Format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '₹0';
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)} K`;
  return `₹${value.toFixed(0)}`;
};

// Severity Badge Component
const SeverityBadge = ({ severity }) => {
  const colors = {
    critical: 'bg-red-600 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-yellow-500 text-black',
    low: 'bg-green-500 text-white'
  };
  return (
    <Badge className={colors[severity] || 'bg-slate-500'}>
      {severity?.toUpperCase()}
    </Badge>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const colors = {
    new: 'bg-blue-600 text-white',
    acknowledged: 'bg-amber-500 text-black',
    resolved: 'bg-emerald-500 text-white',
    escalated: 'bg-red-500 text-white'
  };
  return (
    <Badge className={colors[status] || 'bg-slate-500'}>
      {status?.replace('_', ' ').toUpperCase()}
    </Badge>
  );
};

const AnalyticsPage = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [insights, setInsights] = useState({});
  const [thresholds, setThresholds] = useState({});
  const [editedThresholds, setEditedThresholds] = useState({});
  
  // Filters
  const [alertFilters, setAlertFilters] = useState({
    status: '',
    severity: '',
    insight_type: ''
  });
  
  // Dialogs
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showThresholdDialog, setShowThresholdDialog] = useState(false);
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [runningAnalytics, setRunningAnalytics] = useState(false);
  const [analyticsResults, setAnalyticsResults] = useState(null);
  
  // AI Chat state
  const [query, setQuery] = useState('');
  const [context, setContext] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversation, setConversation] = useState([]);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/analytics/dashboard`, {
        headers: getAuthHeaders()
      });
      setDashboardData(res.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  }, [getAuthHeaders]);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (alertFilters.status) params.append('status', alertFilters.status);
      if (alertFilters.severity) params.append('severity', alertFilters.severity);
      if (alertFilters.insight_type) params.append('insight_type', alertFilters.insight_type);
      
      const res = await axios.get(`${API_URL}/analytics/alerts?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      setAlerts(res.data.alerts || res.data || []);
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  }, [getAuthHeaders, alertFilters]);

  // Fetch thresholds
  const fetchThresholds = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/analytics/thresholds`, {
        headers: getAuthHeaders()
      });
      setThresholds(res.data.thresholds || res.data || {});
      setEditedThresholds(res.data.thresholds || res.data || {});
    } catch (err) {
      console.error('Error fetching thresholds:', err);
    }
  }, [getAuthHeaders]);

  // Fetch insights
  const fetchInsights = useCallback(async (type) => {
    setLoading(true);
    try {
      let endpoint = '';
      switch(type) {
        case 'churn_risk':
          endpoint = '/analytics/insights/churn-risk';
          break;
        case 'dormant_client':
          endpoint = '/analytics/insights/dormant-clients';
          break;
        case 'high_value_client':
          endpoint = '/analytics/insights/high-value-clients';
          break;
        case 'revenue_leakage':
          endpoint = '/analytics/insights/revenue-leakage';
          break;
        case 'dealer_productivity':
          endpoint = '/analytics/insights/dealer-productivity';
          break;
        default:
          return;
      }
      
      const res = await axios.get(`${API_URL}${endpoint}`, {
        headers: getAuthHeaders()
      });
      setInsights(prev => ({
        ...prev,
        [type]: res.data.insights || res.data || []
      }));
    } catch (err) {
      console.error(`Error fetching ${type} insights:`, err);
      toast.error(`Failed to fetch ${type.replace('_', ' ')} insights`);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Run analytics
  const runAnalytics = async () => {
    setRunningAnalytics(true);
    try {
      const res = await axios.post(`${API_URL}/analytics/run`, {}, {
        headers: getAuthHeaders()
      });
      setAnalyticsResults(res.data);
      toast.success(`Analytics completed: ${res.data.summary?.total_insights || 0} insights found`);
      // Refresh data
      fetchDashboard();
      fetchAlerts();
    } catch (err) {
      toast.error('Failed to run analytics');
      console.error('Error running analytics:', err);
    } finally {
      setRunningAnalytics(false);
    }
  };

  // Acknowledge alert
  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API_URL}/analytics/alerts/${alertId}/acknowledge`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Alert acknowledged');
      fetchAlerts();
      fetchDashboard();
      setSelectedAlert(null);
    } catch (err) {
      toast.error('Failed to acknowledge alert');
    }
  };

  // Resolve alert
  const resolveAlert = async (alertId, note = '') => {
    try {
      await axios.put(`${API_URL}/analytics/alerts/${alertId}/resolve`, 
        { resolution_note: note },
        { headers: getAuthHeaders() }
      );
      toast.success('Alert resolved');
      fetchAlerts();
      fetchDashboard();
      setSelectedAlert(null);
    } catch (err) {
      toast.error('Failed to resolve alert');
    }
  };

  // Save thresholds
  const saveThresholds = async () => {
    try {
      await axios.put(`${API_URL}/analytics/thresholds`, editedThresholds, {
        headers: getAuthHeaders()
      });
      toast.success('Thresholds updated successfully');
      setThresholds(editedThresholds);
      setShowThresholdDialog(false);
    } catch (err) {
      toast.error('Failed to save thresholds');
    }
  };

  // AI Query handler
  const handleAIQuery = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    const userMessage = { role: 'user', content: query, timestamp: new Date().toISOString() };
    setConversation(prev => [...prev, userMessage]);
    setChatLoading(true);

    try {
      const response = await axios.post(`${API_URL}/analytics/query`, {
        query: query.trim(),
        context: context.trim() || null
      }, { headers: getAuthHeaders() });

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp,
        id: response.data.id
      };
      
      setConversation(prev => [...prev, aiMessage]);
      setQuery('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process query');
      const errorMessage = {
        role: 'assistant',
        content: error.response?.data?.detail || 'An error occurred while processing your query.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setConversation(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  // Load data on mount and tab change
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboard();
    } else if (activeTab === 'alerts') {
      fetchAlerts();
    } else if (activeTab === 'thresholds') {
      fetchThresholds();
    }
  }, [activeTab, fetchDashboard, fetchAlerts, fetchThresholds]);

  // Sample queries for AI chat
  const sampleQueries = [
    { icon: Users, text: "What is the client distribution by segment?", category: "Clients" },
    { icon: Wallet, text: "Show me the AUM breakdown by city", category: "AUM" },
    { icon: TrendingUp, text: "What are the transaction trends this month?", category: "Transactions" },
    { icon: ShieldCheck, text: "How many clients have pending KYC?", category: "Compliance" },
  ];

  // Prepare chart data
  const severityChartData = dashboardData?.alerts_by_severity ? 
    Object.entries(dashboardData.alerts_by_severity).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      color: SEVERITY_COLORS[name]
    })) : [];

  const statusChartData = dashboardData?.alerts_by_status ?
    Object.entries(dashboardData.alerts_by_status).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      color: STATUS_COLORS[name]
    })) : [];

  return (
    <div className="h-full flex flex-col animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-[#C5A059]" />
            Analytics & Insights
          </h1>
          <p className="text-slate-400 mt-1">
            Rule-based analytics with AI-powered explanations • Non-advisory insights only
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-3">
          {user?.role === 'principal' && (
            <Button
              onClick={() => setShowThresholdDialog(true)}
              variant="outline"
              className="border-slate-600"
            >
              <Settings className="w-4 h-4 mr-2" />
              Thresholds
            </Button>
          )}
          <Button
            onClick={() => setShowRunDialog(true)}
            className="btn-gold"
            disabled={runningAnalytics}
          >
            {runningAnalytics ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <PlayCircle className="w-4 h-4 mr-2" />
            )}
            Run Analytics
          </Button>
        </div>
      </div>

      {/* SEBI Compliance Notice */}
      <Card className="mb-6 bg-amber-500/10 border-amber-500/30">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-400">SEBI Compliance Notice</p>
            <p className="text-sm text-amber-300/70 mt-1">
              This module provides <strong>analytical and informational insights only</strong>. 
              It cannot provide investment advice, buy/sell recommendations, or portfolio rebalancing suggestions.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="bg-slate-800 mb-6">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#C5A059]">
            <PieChartIcon className="w-4 h-4 mr-2" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-[#C5A059]">
            <Bell className="w-4 h-4 mr-2" /> 
            Alerts
            {dashboardData?.new_alerts_count > 0 && (
              <Badge className="ml-2 bg-red-500 text-white">{dashboardData.new_alerts_count}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="insights" className="data-[state=active]:bg-[#C5A059]">
            <Zap className="w-4 h-4 mr-2" /> Insights Explorer
          </TabsTrigger>
          <TabsTrigger value="ai-chat" className="data-[state=active]:bg-[#C5A059]">
            <Sparkles className="w-4 h-4 mr-2" /> AI Assistant
          </TabsTrigger>
          {user?.role === 'principal' && (
            <TabsTrigger value="thresholds" className="data-[state=active]:bg-[#C5A059]">
              <Settings className="w-4 h-4 mr-2" /> Configuration
            </TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="flex-1 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="card-vault">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">New Alerts</p>
                    <p className="text-3xl font-bold text-white">
                      {dashboardData?.new_alerts_count || 0}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-blue-500/20">
                    <BellRing className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-vault">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Critical Alerts</p>
                    <p className="text-3xl font-bold text-red-500">
                      {dashboardData?.critical_alerts_count || 0}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-red-500/20">
                    <AlertCircle className="w-6 h-6 text-red-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-vault">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Last Run</p>
                    <p className="text-lg font-medium text-white">
                      {dashboardData?.last_run 
                        ? new Date(dashboardData.last_run).toLocaleDateString()
                        : 'Never'}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-emerald-500/20">
                    <Activity className="w-6 h-6 text-emerald-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-vault">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total Insights</p>
                    <p className="text-3xl font-bold text-[#C5A059]">
                      {dashboardData?.last_run_summary?.total_insights || 0}
                    </p>
                  </div>
                  <div className="p-3 rounded-full bg-[#C5A059]/20">
                    <Zap className="w-6 h-6 text-[#C5A059]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Alerts by Severity */}
            <Card className="card-vault">
              <CardHeader>
                <CardTitle className="text-white text-lg">Alerts by Severity</CardTitle>
              </CardHeader>
              <CardContent>
                {severityChartData.length > 0 ? (
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={severityChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {severityChartData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-400">
                    No alerts to display
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Alerts by Status */}
            <Card className="card-vault">
              <CardHeader>
                <CardTitle className="text-white text-lg">Alerts by Status</CardTitle>
              </CardHeader>
              <CardContent>
                {statusChartData.length > 0 ? (
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={statusChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="name" tick={{ fill: '#94a3b8' }} />
                        <YAxis tick={{ fill: '#94a3b8' }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }} />
                        <Bar dataKey="value" fill="#C5A059" radius={[4, 4, 0, 0]}>
                          {statusChartData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-400">
                    No alerts to display
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Alerts */}
          <Card className="card-vault">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg">Recent Alerts</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setActiveTab('alerts')}>
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {dashboardData?.recent_alerts?.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.recent_alerts.slice(0, 5).map((alert) => (
                    <div 
                      key={alert.id}
                      className="p-3 rounded-lg bg-white/5 border border-white/10 hover:border-[#C5A059]/30 transition-colors cursor-pointer"
                      onClick={() => setSelectedAlert(alert)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <SeverityBadge severity={alert.severity} />
                          <span className="text-white font-medium">{alert.title}</span>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(alert.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400 mt-2 line-clamp-1">{alert.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-slate-400">
                  <Bell className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No recent alerts. Run analytics to generate insights.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="flex-1 space-y-6">
          {/* Filters */}
          <Card className="card-vault">
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="w-40">
                  <label className="text-xs text-slate-400 block mb-1">Status</label>
                  <Select 
                    value={alertFilters.status || 'all'} 
                    onValueChange={(v) => setAlertFilters({...alertFilters, status: v === 'all' ? '' : v})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="acknowledged">Acknowledged</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="escalated">Escalated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <label className="text-xs text-slate-400 block mb-1">Severity</label>
                  <Select 
                    value={alertFilters.severity || 'all'} 
                    onValueChange={(v) => setAlertFilters({...alertFilters, severity: v === 'all' ? '' : v})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600">
                      <SelectValue placeholder="All Severity" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      <SelectItem value="all">All Severity</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-48">
                  <label className="text-xs text-slate-400 block mb-1">Type</label>
                  <Select 
                    value={alertFilters.insight_type || 'all'} 
                    onValueChange={(v) => setAlertFilters({...alertFilters, insight_type: v === 'all' ? '' : v})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="churn_risk">Churn Risk</SelectItem>
                      <SelectItem value="dormant_client">Dormant Client</SelectItem>
                      <SelectItem value="high_value_client">High Value Client</SelectItem>
                      <SelectItem value="revenue_leakage">Revenue Leakage</SelectItem>
                      <SelectItem value="dealer_productivity">Dealer Productivity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchAlerts} variant="outline" className="border-slate-600">
                  <Filter className="w-4 h-4 mr-2" /> Apply Filters
                </Button>
                <Button 
                  onClick={() => setAlertFilters({ status: '', severity: '', insight_type: '' })}
                  variant="ghost"
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Alerts Table */}
          <Card className="card-vault">
            <CardHeader>
              <CardTitle className="text-white">Alerts ({alerts.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">Severity</TableHead>
                    <TableHead className="text-slate-400">Type</TableHead>
                    <TableHead className="text-slate-400">Title</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Created</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.length > 0 ? alerts.map((alert) => (
                    <TableRow key={alert.id} className="border-slate-700 hover:bg-white/5">
                      <TableCell><SeverityBadge severity={alert.severity} /></TableCell>
                      <TableCell className="text-slate-300 capitalize">
                        {alert.insight_type?.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell className="text-white max-w-xs truncate">{alert.title}</TableCell>
                      <TableCell><StatusBadge status={alert.status} /></TableCell>
                      <TableCell className="text-slate-400">
                        {new Date(alert.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedAlert(alert)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                        No alerts found. Try adjusting filters or run analytics.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insights Explorer Tab */}
        <TabsContent value="insights" className="flex-1 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Churn Risk */}
            <Card className="card-vault cursor-pointer hover:border-[#C5A059]/50 transition-colors"
                  onClick={() => fetchInsights('churn_risk')}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-500/20">
                      <UserX className="w-5 h-5 text-red-500" />
                    </div>
                    <CardTitle className="text-white text-lg">Churn Risk</CardTitle>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 text-sm">
                  Identify clients at risk of leaving based on inactivity, AUM decline, and brokerage drop.
                </p>
                {insights.churn_risk && (
                  <Badge className="mt-3 bg-red-500/20 text-red-400">
                    {insights.churn_risk.length} clients at risk
                  </Badge>
                )}
              </CardContent>
            </Card>

            {/* Dormant Clients */}
            <Card className="card-vault cursor-pointer hover:border-[#C5A059]/50 transition-colors"
                  onClick={() => fetchInsights('dormant_client')}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      <Clock className="w-5 h-5 text-amber-500" />
                    </div>
                    <CardTitle className="text-white text-lg">Dormant Clients</CardTitle>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 text-sm">
                  Find clients with no recent trading activity who may need re-engagement.
                </p>
                {insights.dormant_client && (
                  <Badge className="mt-3 bg-amber-500/20 text-amber-400">
                    {insights.dormant_client.length} dormant clients
                  </Badge>
                )}
              </CardContent>
            </Card>

            {/* High Value Clients */}
            <Card className="card-vault cursor-pointer hover:border-[#C5A059]/50 transition-colors"
                  onClick={() => fetchInsights('high_value_client')}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/20">
                      <Crown className="w-5 h-5 text-emerald-500" />
                    </div>
                    <CardTitle className="text-white text-lg">High Value Clients</CardTitle>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 text-sm">
                  Identify HNI and UHNI clients for priority service and attention.
                </p>
                {insights.high_value_client && (
                  <Badge className="mt-3 bg-emerald-500/20 text-emerald-400">
                    {insights.high_value_client.length} high value clients
                  </Badge>
                )}
              </CardContent>
            </Card>

            {/* Revenue Leakage */}
            <Card className="card-vault cursor-pointer hover:border-[#C5A059]/50 transition-colors"
                  onClick={() => fetchInsights('revenue_leakage')}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-violet-500/20">
                      <DollarSign className="w-5 h-5 text-violet-500" />
                    </div>
                    <CardTitle className="text-white text-lg">Revenue Leakage</CardTitle>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 text-sm">
                  Detect brokerage variances that may indicate revenue leakage or errors.
                </p>
                {insights.revenue_leakage && (
                  <Badge className="mt-3 bg-violet-500/20 text-violet-400">
                    {insights.revenue_leakage.length} variances detected
                  </Badge>
                )}
              </CardContent>
            </Card>

            {/* Dealer Productivity */}
            <Card className="card-vault cursor-pointer hover:border-[#C5A059]/50 transition-colors"
                  onClick={() => fetchInsights('dealer_productivity')}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <Target className="w-5 h-5 text-blue-500" />
                    </div>
                    <CardTitle className="text-white text-lg">Dealer Productivity</CardTitle>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 text-sm">
                  Analyze dealer performance scores based on clients, brokerage, and targets.
                </p>
                {insights.dealer_productivity && (
                  <Badge className="mt-3 bg-blue-500/20 text-blue-400">
                    {insights.dealer_productivity.length} dealers analyzed
                  </Badge>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Insights Results */}
          {loading && (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 mx-auto text-[#C5A059] animate-spin mb-4" />
              <p className="text-slate-400">Analyzing data...</p>
            </div>
          )}

          {/* Display loaded insights */}
          {Object.entries(insights).map(([type, data]) => (
            data && data.length > 0 && (
              <Card key={type} className="card-vault">
                <CardHeader>
                  <CardTitle className="text-white capitalize flex items-center gap-2">
                    {(() => {
                      const Icon = INSIGHT_ICONS[type] || Zap;
                      return <Icon className="w-5 h-5 text-[#C5A059]" />;
                    })()}
                    {type.replace(/_/g, ' ')} Results ({data.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Severity</TableHead>
                        <TableHead className="text-slate-400">
                          {type === 'dealer_productivity' ? 'Dealer' : 'Client'}
                        </TableHead>
                        <TableHead className="text-slate-400">Details</TableHead>
                        <TableHead className="text-slate-400 text-right">
                          {type === 'dealer_productivity' ? 'Score' : 'AUM'}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.slice(0, 10).map((item, idx) => (
                        <TableRow key={idx} className="border-slate-700">
                          <TableCell><SeverityBadge severity={item.severity} /></TableCell>
                          <TableCell className="text-white">
                            {item.client_name || item.dealer_name || item.client_code || 'N/A'}
                          </TableCell>
                          <TableCell className="text-slate-300 max-w-md">
                            {type === 'churn_risk' && item.risk_factors?.join(', ')}
                            {type === 'dormant_client' && `Dormant for ${item.days_dormant} days`}
                            {type === 'high_value_client' && (item.is_ultra_high_value ? 'Ultra High Value' : 'High Value')}
                            {type === 'revenue_leakage' && `${item.variance_percent?.toFixed(1)}% variance`}
                            {type === 'dealer_productivity' && `${item.productivity_band} productivity`}
                          </TableCell>
                          <TableCell className="text-right text-[#C5A059]">
                            {type === 'dealer_productivity' 
                              ? `${item.productivity_score?.toFixed(0)}/100`
                              : formatCurrency(item.aum || item.current_aum || 0)
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )
          ))}
        </TabsContent>

        {/* AI Chat Tab */}
        <TabsContent value="ai-chat" className="flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-full min-h-[500px]">
            {/* Chat Area */}
            <Card className="card-vault lg:col-span-3 flex flex-col">
              <CardHeader className="border-b border-white/5">
                <CardTitle className="font-heading text-white flex items-center gap-2">
                  <Bot className="w-5 h-5 text-[#C5A059]" />
                  Analytics Assistant
                </CardTitle>
              </CardHeader>
              
              <ScrollArea className="flex-1 p-4">
                {conversation.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center py-12">
                    <Sparkles className="w-16 h-16 text-[#C5A059]/30 mb-4" />
                    <h3 className="font-heading text-lg text-white mb-2">
                      Start Exploring Your Data
                    </h3>
                    <p className="text-slate-400 max-w-md">
                      Ask questions about your clients, AUM, transactions, or compliance status. 
                      Try one of the sample queries to get started.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {conversation.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-4 ${
                            msg.role === 'user'
                              ? 'bg-[#C5A059]/20 border border-[#C5A059]/30'
                              : msg.isError
                              ? 'bg-red-500/10 border border-red-500/30'
                              : 'bg-white/5 border border-white/10'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            {msg.role === 'user' ? (
                              <User className="w-4 h-4 text-[#C5A059]" />
                            ) : (
                              <Bot className={`w-4 h-4 ${msg.isError ? 'text-red-500' : 'text-sky-500'}`} />
                            )}
                            <span className="text-xs text-slate-500 font-mono">
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <div className={`text-sm whitespace-pre-wrap ${
                            msg.role === 'user' ? 'text-white' : msg.isError ? 'text-red-400' : 'text-slate-300'
                          }`}>
                            {msg.content}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {chatLoading && (
                      <div className="flex justify-start">
                        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 text-[#C5A059] animate-spin" />
                            <span className="text-sm text-slate-400">Analyzing...</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
              
              {/* Input Area */}
              <div className="p-4 border-t border-white/5">
                <form onSubmit={handleAIQuery} className="space-y-3">
                  <Textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Ask about your client data, AUM, transactions, or compliance..."
                    className="input-vault min-h-[80px] resize-none"
                    disabled={chatLoading}
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-slate-500">
                      <Clock className="w-3 h-3 inline mr-1" />
                      All queries are logged for audit compliance
                    </p>
                    <Button
                      type="submit"
                      className="btn-gold"
                      disabled={chatLoading || !query.trim()}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Analyze
                    </Button>
                  </div>
                </form>
              </div>
            </Card>

            {/* Sidebar */}
            <div className="space-y-4">
              <Card className="card-vault">
                <CardHeader>
                  <CardTitle className="font-heading text-white text-sm">
                    Sample Queries
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {sampleQueries.map((sample, idx) => (
                    <button
                      key={idx}
                      onClick={() => setQuery(sample.text)}
                      className="w-full text-left p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-[#C5A059]/30 transition-colors group"
                    >
                      <div className="flex items-start gap-3">
                        <sample.icon className="w-4 h-4 text-[#C5A059] mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500 mb-1">{sample.category}</p>
                          <p className="text-sm text-slate-300 group-hover:text-white transition-colors">
                            {sample.text}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </CardContent>
              </Card>

              <Card className="card-vault">
                <CardHeader>
                  <CardTitle className="font-heading text-white text-sm">
                    Allowed Topics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-slate-400">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Client demographics & segmentation
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      AUM distribution analysis
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Transaction summaries
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      KYC status reports
                    </li>
                  </ul>
                  
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <p className="text-xs text-red-400">
                      ⚠️ Cannot provide: Investment advice, buy/sell recommendations
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Thresholds Tab (Principal Only) */}
        {user?.role === 'principal' && (
          <TabsContent value="thresholds" className="flex-1 space-y-6">
            <Card className="card-vault">
              <CardHeader>
                <CardTitle className="text-white">Analytics Threshold Configuration</CardTitle>
                <CardDescription>
                  Configure the thresholds used for generating analytics insights and alerts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Churn Risk */}
                  <div className="space-y-4">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <UserX className="w-4 h-4 text-red-500" />
                      Churn Risk
                    </h4>
                    <div>
                      <label className="text-xs text-slate-400">Inactive Days</label>
                      <Input
                        type="number"
                        value={editedThresholds.churn_inactive_days || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          churn_inactive_days: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">AUM Decline %</label>
                      <Input
                        type="number"
                        value={editedThresholds.churn_aum_decline_percent || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          churn_aum_decline_percent: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                  </div>

                  {/* Dormant Clients */}
                  <div className="space-y-4">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-500" />
                      Dormant Clients
                    </h4>
                    <div>
                      <label className="text-xs text-slate-400">Dormant Days</label>
                      <Input
                        type="number"
                        value={editedThresholds.dormant_days || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          dormant_days: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Critical Dormant Days</label>
                      <Input
                        type="number"
                        value={editedThresholds.dormant_critical_days || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          dormant_critical_days: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                  </div>

                  {/* High Value Clients */}
                  <div className="space-y-4">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <Crown className="w-4 h-4 text-emerald-500" />
                      High Value Clients
                    </h4>
                    <div>
                      <label className="text-xs text-slate-400">HNI AUM Threshold (₹)</label>
                      <Input
                        type="number"
                        value={editedThresholds.high_value_aum_threshold || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          high_value_aum_threshold: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">UHNI AUM Threshold (₹)</label>
                      <Input
                        type="number"
                        value={editedThresholds.ultra_high_value_aum_threshold || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          ultra_high_value_aum_threshold: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                  </div>

                  {/* Revenue Leakage */}
                  <div className="space-y-4">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-violet-500" />
                      Revenue Leakage
                    </h4>
                    <div>
                      <label className="text-xs text-slate-400">Variance %</label>
                      <Input
                        type="number"
                        value={editedThresholds.leakage_variance_percent || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          leakage_variance_percent: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Minimum Amount (₹)</label>
                      <Input
                        type="number"
                        value={editedThresholds.leakage_minimum_amount || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          leakage_minimum_amount: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                  </div>

                  {/* Dealer Productivity */}
                  <div className="space-y-4">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <Target className="w-4 h-4 text-blue-500" />
                      Dealer Productivity
                    </h4>
                    <div>
                      <label className="text-xs text-slate-400">Min Clients</label>
                      <Input
                        type="number"
                        value={editedThresholds.dealer_min_clients || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          dealer_min_clients: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Min Monthly Brokerage (₹)</label>
                      <Input
                        type="number"
                        value={editedThresholds.dealer_min_monthly_brokerage || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          dealer_min_monthly_brokerage: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Low Productivity Threshold %</label>
                      <Input
                        type="number"
                        value={editedThresholds.dealer_target_achievement_low || ''}
                        onChange={(e) => setEditedThresholds({
                          ...editedThresholds,
                          dealer_target_achievement_low: parseInt(e.target.value) || 0
                        })}
                        className="input-vault"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="border-t border-white/5 pt-6">
                <div className="flex items-center justify-between w-full">
                  <p className="text-xs text-slate-400">
                    <Info className="w-3 h-3 inline mr-1" />
                    Changes will apply to future analytics runs
                  </p>
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setEditedThresholds(thresholds)}
                      className="border-slate-600"
                    >
                      Reset
                    </Button>
                    <Button onClick={saveThresholds} className="btn-gold">
                      <Save className="w-4 h-4 mr-2" />
                      Save Thresholds
                    </Button>
                  </div>
                </div>
              </CardFooter>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Alert Detail Dialog */}
      <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <SeverityBadge severity={selectedAlert?.severity} />
              {selectedAlert?.title}
            </DialogTitle>
            <DialogDescription>
              {selectedAlert?.insight_type?.replace(/_/g, ' ')} • 
              Created {selectedAlert?.created_at && new Date(selectedAlert.created_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-white/5">
              <p className="text-slate-300">{selectedAlert?.description}</p>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">Status:</span>
              <StatusBadge status={selectedAlert?.status} />
            </div>
            
            {selectedAlert?.acknowledged_at && (
              <div className="text-sm text-slate-400">
                Acknowledged by {selectedAlert.acknowledged_by_email} on {new Date(selectedAlert.acknowledged_at).toLocaleString()}
              </div>
            )}
            
            {selectedAlert?.resolved_at && (
              <div className="text-sm text-slate-400">
                Resolved by {selectedAlert.resolved_by_email} on {new Date(selectedAlert.resolved_at).toLocaleString()}
                {selectedAlert.resolution_note && (
                  <p className="mt-1 text-slate-500">Note: {selectedAlert.resolution_note}</p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedAlert(null)}>
              Close
            </Button>
            {selectedAlert?.status === 'new' && (
              <Button 
                onClick={() => acknowledgeAlert(selectedAlert.id)}
                className="bg-amber-500 hover:bg-amber-600"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Acknowledge
              </Button>
            )}
            {(selectedAlert?.status === 'new' || selectedAlert?.status === 'acknowledged') && (
              <Button 
                onClick={() => resolveAlert(selectedAlert.id)}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Resolve
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Analytics Dialog */}
      <Dialog open={showRunDialog} onOpenChange={setShowRunDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Run Analytics</DialogTitle>
            <DialogDescription>
              This will analyze all client and dealer data to generate insights and alerts.
            </DialogDescription>
          </DialogHeader>
          
          {analyticsResults ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="font-medium text-emerald-400 mb-2">Analytics Complete!</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Total Insights</p>
                    <p className="text-white text-lg">{analyticsResults.summary?.total_insights || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Alerts Created</p>
                    <p className="text-white text-lg">{analyticsResults.alerts_created || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Critical</p>
                    <p className="text-red-400 text-lg">{analyticsResults.summary?.critical_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">High Priority</p>
                    <p className="text-orange-400 text-lg">{analyticsResults.summary?.high_count || 0}</p>
                  </div>
                </div>
              </div>
              <Button onClick={() => { setShowRunDialog(false); setAnalyticsResults(null); }} className="w-full">
                Close
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-3 text-sm text-slate-300">
                <p>The following analyses will be performed:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <UserX className="w-4 h-4 text-red-500" /> Churn Risk Detection
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500" /> Dormant Client Identification
                  </li>
                  <li className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-emerald-500" /> High Value Client Analysis
                  </li>
                  <li className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-violet-500" /> Revenue Leakage Detection
                  </li>
                  <li className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-500" /> Dealer Productivity Scoring
                  </li>
                </ul>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRunDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={runAnalytics} 
                  disabled={runningAnalytics}
                  className="btn-gold"
                >
                  {runningAnalytics ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <PlayCircle className="w-4 h-4 mr-2" />
                  )}
                  {runningAnalytics ? 'Running...' : 'Run Analytics'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnalyticsPage;
